import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import AbbreviatedNumberTooltip from '../Common/AbbreviatedNumberTooltip';
import { chartLineStyles } from '../Common/ChartUtils';
import PercentTooltip from '../Common/PercentTooltip';
import { calculateRate, formatChartData, getElements } from '../PartnerReports.utils';
import { reportNameToTableHeaderMap } from './CloudChurnOrRenewals.types';
export const calculateTotalCount = function calculateTotalCount() {
  let elements = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return elements.reduce((total, current) => current.count + total, 0);
};
export const getCloudReport = (reportName, apiResponse) => {
  const reportHeader = reportNameToTableHeaderMap[reportName];
  let total;
  let actual;
  let rate;
  if (apiResponse) {
    const totalSeries = apiResponse.series.find(item => item.name === reportHeader.total);
    total = (totalSeries === null || totalSeries === void 0 ? void 0 : totalSeries.uniqueTotal) || calculateTotalCount(totalSeries === null || totalSeries === void 0 ? void 0 : totalSeries.elements);
    const actualSeries = apiResponse.series.find(item => item.name === reportHeader.actual);
    actual = (actualSeries === null || actualSeries === void 0 ? void 0 : actualSeries.uniqueTotal) || calculateTotalCount(actualSeries === null || actualSeries === void 0 ? void 0 : actualSeries.elements);
    rate = calculateRate(total, actual);
  }
  return {
    total,
    actual,
    rate
  };
};
export const getCloudChurnOrRenewalTableData = (apiResponse, reportName, reportingApps) => {
  const tableData = apiResponse.addons.map(response => {
    const annualReport = getCloudReport(reportName, response.datasets.find(item => item.name === 'Annual'));
    const monthlyReport = getCloudReport(reportName, response.datasets.find(item => item.name === 'Monthly'));
    return {
      appKey: response.addonKey,
      appName: response.name,
      monthly: monthlyReport,
      annual: annualReport
    };
  });
  reportingApps.forEach(app => {
    if (!tableData.some(entry => entry.appKey === app.appKey)) {
      tableData.push({
        appKey: app.appKey,
        appName: app.appName,
        monthly: getCloudReport(reportName),
        annual: getCloudReport(reportName)
      });
    }
  });
  return tableData;
};
export const getChartLinesDetails = (canUseLinePatterns, reportName) => {
  const lineStyles = chartLineStyles.slice(0, 2);
  return [_objectSpread({
    color: lineStyles[0].color,
    dataKey: 'MonthlyRate',
    legend: reportNameToTableHeaderMap[reportName].rateMonthly.commonLabel
  }, canUseLinePatterns ? {
    dotStyle: lineStyles[0].dotStyle,
    strokeStyle: lineStyles[0].strokeStyle
  } : {}), _objectSpread({
    color: lineStyles[1].color,
    dataKey: 'AnnualRate',
    legend: reportNameToTableHeaderMap[reportName].rateAnnual.commonLabel
  }, canUseLinePatterns ? {
    dotStyle: lineStyles[1].dotStyle,
    strokeStyle: lineStyles[1].strokeStyle
  } : {})];
};
const getCountFromElements = element => element ? element.count : 0;
export const getChartData = (apiResponse, reportName, appKey) => {
  const reportHeader = reportNameToTableHeaderMap[reportName];
  const datasets = getDatasetsByAppName(apiResponse, appKey);

  // If there are no datasets for given app key, it will return empty chart data.
  if (datasets.length === 0) {
    return [];
  }
  const lineChartData = datasets.map(dataset => dataset.series[0].elements.map((element, index) => {
    const total = getCountFromElements(getElements(dataset.series, reportHeader.total)[index]);
    const actual = getCountFromElements(getElements(dataset.series, reportHeader.actual)[index]);
    const rate = calculateRate(total, actual);
    return {
      ["".concat(dataset.name, "Total")]: total,
      ["".concat(dataset.name, "Actual")]: actual,
      ["".concat(dataset.name, "Rate")]: rate,
      xLabel: element.date
    };
  }));
  return formatChartData(lineChartData);
};
const getValueFromPayload = (item, term, type) => {
  const value = item.payload[term + type];
  return typeof value === 'number' ? value : 0;
};
export const getChartTooltipPairs = (payload, lines) => payload.map((item, index) => {
  const term = item.dataKey.split('Rate')[0];
  const rate = getValueFromPayload(item, term, 'Rate');
  const actual = getValueFromPayload(item, term, 'Actual');
  const total = getValueFromPayload(item, term, 'Total');
  return {
    color: item.color,
    label: item.name,
    value: "".concat(rate, "% (").concat(actual, " / ").concat(total, ")"),
    isCharted: true,
    dotStyle: lines[index].dotStyle,
    strokeStyle: lines[index].strokeStyle
  };
});
export const getColumns = (monthlyReport, reportName) => {
  const headerConfig = reportNameToTableHeaderMap[reportName];
  return [{
    key: headerConfig.total,
    value: monthlyReport.total,
    content: /*#__PURE__*/React.createElement(AbbreviatedNumberTooltip, {
      value: monthlyReport.total
    })
  }, {
    key: headerConfig.actual,
    value: monthlyReport.actual,
    content: /*#__PURE__*/React.createElement(AbbreviatedNumberTooltip, {
      value: monthlyReport.actual
    })
  }, {
    key: headerConfig.rate,
    value: monthlyReport.rate,
    content: /*#__PURE__*/React.createElement(PercentTooltip, {
      rate: monthlyReport.rate
    })
  }];
};
export const getOverviewColumns = (monthlyOrAnnualReport, reportName, reportType) => {
  const headerConfig = reportNameToTableHeaderMap[reportName];
  return [{
    key: reportType === 'monthly' ? headerConfig.rateMonthly.commonLabel : headerConfig.rateAnnual.commonLabel,
    content: /*#__PURE__*/React.createElement(PercentTooltip, {
      rate: monthlyOrAnnualReport.rate
    })
  }];
};
export const getReportColumnsForOverviewSeries = (datasets, reportName) => {
  const monthlyReport = getCloudReport(reportName, datasets.find(item => item.name === 'Monthly'));
  const annualReport = getCloudReport(reportName, datasets.find(item => item.name === 'Annual'));
  const monthlyColumns = getOverviewColumns(monthlyReport, reportName, 'monthly');
  const annualColumns = getOverviewColumns(annualReport, reportName, 'annual');
  return [...monthlyColumns, ...annualColumns];
};
export const getReportColumnsForSeries = (datasets, reportName) => {
  const monthlyReport = getCloudReport(reportName, datasets.find(item => item.name === 'Monthly'));
  const annualReport = getCloudReport(reportName, datasets.find(item => item.name === 'Annual'));
  const monthlyColumns = getColumns(monthlyReport, reportName);
  const annualColumns = getColumns(annualReport, reportName);
  return [...monthlyColumns, ...annualColumns];
};

/*
 * This function will return Aggregated series for given app
 * If no app passed, will retutn total series
 */
export const getDatasetsByAppName = (apiResponse, appKey) => {
  if (appKey) {
    const responseByAppKey = apiResponse.addons.find(dataset => dataset.addonKey === appKey);
    return responseByAppKey ? responseByAppKey.datasets : [];
  }
  return apiResponse.total.datasets;
};